<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-screen max-w-2xl' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="formState" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-start justify-between">
                        <div class="space-y-0.5">
                            <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Step {{ step }} of 2</p>
                            <h3 class="text-base font-medium leading-6 text-gray-800">{{ heading }}</h3>
                        </div>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                    </div>
                    <div class="mt-3 grid grid-cols-2 gap-1.5">
                        <div class="h-1 rounded-full bg-primary-500" />
                        <div class="h-1 rounded-full transition-colors" :class="step === 2 ? 'bg-primary-500' : 'bg-gray-200'" />
                    </div>
                </template>

                <div v-if="step === 1" class="space-y-4">
                    <p class="text-sm text-gray-600">How do you want to onboard this doctor?</p>
                    <button
                        v-for="option in paths"
                        :key="option.value"
                        type="button"
                        @click="path = option.value"
                        @dblclick="next()"
                        class="w-full rounded-lg p-5 text-left transition"
                        :class="path === option.value ? 'bg-primary-50/50 ring-2 ring-primary-500' : 'ring-1 ring-gray-200 hover:bg-gray-50 hover:ring-gray-300'"
                    >
                        <div class="flex items-start gap-4">
                            <div
                                class="flex size-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors"
                                :class="path === option.value ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'"
                            >
                                <UIcon :name="option.icon" class="size-6" />
                            </div>
                            <div class="flex-1">
                                <p class="font-semibold text-gray-800">{{ option.title }}</p>
                                <p class="mt-1 text-sm text-gray-500">{{ option.description }}</p>
                            </div>
                            <UIcon
                                :name="path === option.value ? 'i-ph-check-circle-fill' : 'i-ph-circle'"
                                class="size-5 flex-shrink-0"
                                :class="path === option.value ? 'text-primary-500' : 'text-gray-300'"
                            />
                        </div>
                    </button>
                </div>

                <div v-else class="space-y-6">
                    <UAlert
                        v-if="serverBlockers.length"
                        color="red"
                        variant="subtle"
                        icon="i-ph-warning-duotone"
                        title="The profile is not ready to publish"
                    >
                        <template #description>
                            <ul class="list-disc pl-4">
                                <li v-for="blocker in serverBlockers" :key="blocker">{{ blocker }}</li>
                            </ul>
                        </template>
                    </UAlert>

                    <template v-if="path === DoctorOnboardingPath.invite">
                        <div class="rounded-lg bg-gray-50 p-4 ring-1 ring-inset ring-gray-200">
                            <p class="text-sm font-medium text-gray-700">What happens next</p>
                            <ol class="mt-2 space-y-1.5 text-sm text-gray-500">
                                <li v-for="(text, index) in inviteSteps" :key="index" class="flex gap-2.5">
                                    <span class="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-600 ring-1 ring-gray-200">{{ index + 1 }}</span>
                                    <span>{{ text }}</span>
                                </li>
                            </ol>
                        </div>

                        <UFormGroup name="full_name" label="Full name" required>
                            <UInput v-model="fields.full_name" placeholder="Rania Haddad" size="lg" />
                        </UFormGroup>
                        <UFormGroup name="email" label="Email" help="The access email is sent here. It becomes the doctor’s login." required>
                            <UInput v-model="email" type="email" placeholder="doctor@example.com" icon="i-ph-envelope-simple-duotone" size="lg" />
                        </UFormGroup>
                        <div class="grid gap-4 sm:grid-cols-2">
                            <UFormGroup
                                name="specialty_id"
                                label="Specialty"
                                hint="Optional"
                                :help="specialtiesUnavailable ? 'Specialties could not be loaded. You can set it later.' : undefined"
                            >
                                <USelectMenu
                                    v-model="fields.specialty_id"
                                    :options="activeSpecialties"
                                    value-attribute="id"
                                    option-attribute="name_en"
                                    searchable
                                    placeholder="Select a specialty"
                                    size="lg"
                                />
                            </UFormGroup>
                            <UFormGroup name="session_price_usd" label="Session price" hint="Optional">
                                <UInput
                                    v-model="fields.session_price_usd"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    size="lg"
                                    :ui="{ leading: { padding: { lg: 'ps-8' } }, trailing: { padding: { lg: 'pe-12' } } }"
                                >
                                    <template #leading>
                                        <span class="text-gray-500">$</span>
                                    </template>
                                    <template #trailing>
                                        <span class="text-xs text-gray-400">USD</span>
                                    </template>
                                </UInput>
                            </UFormGroup>
                        </div>
                    </template>

                    <template v-else>
                        <UFormGroup name="email" label="Email" help="Used for the doctor’s login. They still get an email with a 6-digit code to activate it." required>
                            <UInput v-model="email" type="email" placeholder="doctor@example.com" icon="i-ph-envelope-simple-duotone" size="lg" />
                        </UFormGroup>

                        <TelemedicineDoctorProfileForm v-model="fields" mode="admin" :specialties="specialties" :specialties-unavailable="specialtiesUnavailable" />

                        <p class="flex items-center gap-2 text-xs text-gray-500">
                            <UIcon name="i-ph-image-duotone" class="size-4" />
                            You can add a profile photo from the doctor’s page once it is created.
                        </p>

                        <div class="space-y-3 rounded-lg p-4 ring-1 ring-inset" :class="publish ? 'bg-green-50/50 ring-green-200' : 'ring-gray-200'">
                            <UCheckbox
                                v-model="publish"
                                :disabled="publishBlockersLive.length > 0"
                                label="Publish immediately"
                                help="The doctor appears in the SAMU app as soon as the profile is created."
                            />
                            <ul v-if="publishBlockersLive.length" class="space-y-1 pl-6">
                                <li v-for="blocker in publishBlockersLive" :key="blocker" class="flex items-center gap-2 text-sm text-red-600">
                                    <UIcon name="i-ph-x-circle-duotone" class="size-4 flex-shrink-0" />
                                    {{ blocker }}
                                </li>
                            </ul>
                        </div>
                    </template>
                </div>

                <template #footer>
                    <div class="flex items-center justify-between space-x-2">
                        <UButton v-if="step === 2" @click="back()" size="lg" color="white" icon="i-ph-arrow-left-bold">
                            Back
                        </UButton>
                        <UButton v-else @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>

                        <UButton v-if="step === 1" @click="next()" :disabled="!path" size="lg" color="black" trailing-icon="i-ph-arrow-right-bold">
                            Continue
                        </UButton>
                        <UButton v-else type="submit" size="lg" :loading="loading" color="black" :icon="submitIcon">
                            {{ submitLabel }}
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { DoctorOnboardingPath, type DoctorProfileFields, type Specialty } from '~/types/telemedicine'
import { doctorProfileFormSchema, normalizeDoctorFields, publishBlockers } from '~/utils/telemedicineDoctor'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    specialties: Specialty[]
    specialtiesUnavailable?: boolean
}>()

const toast = useToast()
const { portalLinkAction } = useDoctorActions()

const paths = [
    {
        value: DoctorOnboardingPath.invite,
        title: 'Invite the doctor',
        description: 'They receive an email with a 6-digit code, set a password and complete their own profile. You set the price and publish.',
        icon: 'i-ph-envelope-simple-duotone',
    },
    {
        value: DoctorOnboardingPath.adminBuilt,
        title: 'Build the profile yourself',
        description: 'Fill in everything now and publish immediately. The doctor still gets an email with a 6-digit code to activate their login.',
        icon: 'i-ph-user-gear-duotone',
    },
]

type CreateResult = { id: string, email_sent: true } | { id: string, email_sent: false, email_error: string }

const blankFields = (): DoctorProfileFields => ({
    title: 'Dr.',
    full_name: '',
    specialty_id: null,
    license_number: null,
    years_experience: null,
    languages: [],
    bio_en: null,
    bio_ar: null,
    photo_path: null,
    session_price_usd: 0,
    session_duration_min: 20,
    buffer_min: 5,
})

const form = ref()
const step = ref<1 | 2>(1)
const path = ref<DoctorOnboardingPath>()
const fields = ref<DoctorProfileFields>(blankFields())
const email = ref('')
const publish = ref(false)
const loading = ref(false)
const serverBlockers = ref<string[]>([])

const emailSchema = z.string().trim().email('Enter a valid email address')

const schema = computed(() => (path.value === DoctorOnboardingPath.invite
    ? z.object({
        full_name: z.string().trim().min(2, 'Enter the doctor’s full name'),
        email: emailSchema,
        session_price_usd: z.union([z.number().min(0, 'Price cannot be negative'), z.literal('')]),
    })
    : doctorProfileFormSchema.extend({ email: emailSchema })))

const formState = computed(() => ({ ...fields.value, email: email.value }))

const activeSpecialties = computed(() => props.specialties.filter((s) => s.active))

const publishBlockersLive = computed(() => publishBlockers({ ...normalizeDoctorFields(fields.value), user_id: 'pending' }))

const heading = computed(() => {
    if (step.value === 1) return 'New doctor'
    return path.value === DoctorOnboardingPath.invite ? 'Invite the doctor' : 'Build the profile'
})

const inviteSteps = computed(() => [
    `We email ${email.value.trim() || 'the doctor'} a 6-digit code to set their password.`,
    'You send them the portal sign-in link; they enter the code there and complete their own profile.',
    'You review it, set the price and publish.',
])

const submitLabel = computed(() => {
    if (path.value === DoctorOnboardingPath.invite) return 'Send invitation'
    return publish.value ? 'Create and publish' : 'Create doctor'
})

const submitIcon = computed(() => (path.value === DoctorOnboardingPath.invite ? 'i-ph-paper-plane-tilt-duotone' : 'i-ph-check-bold'))

watch(() => props.open, (open) => {
    if (!open) return
    step.value = 1
    path.value = undefined
    fields.value = blankFields()
    email.value = ''
    publish.value = false
    serverBlockers.value = []
})

watch(publishBlockersLive, (blockers) => {
    if (blockers.length) publish.value = false
})

function next() {
    if (!path.value) return
    step.value = 2
}

function back() {
    form.value?.clear()
    serverBlockers.value = []
    step.value = 1
}

function payload() {
    if (path.value === DoctorOnboardingPath.invite) {
        return {
            path: path.value,
            full_name: fields.value.full_name.trim(),
            email: email.value.trim(),
            specialty_id: fields.value.specialty_id || null,
            session_price_usd: Number(fields.value.session_price_usd) || 0,
        }
    }
    const { photo_path, ...profile } = normalizeDoctorFields(fields.value)
    return { path: path.value, email: email.value.trim(), ...profile, publish: publish.value }
}

async function onSubmit() {
    loading.value = true
    serverBlockers.value = []
    const sentTo = email.value.trim()

    try {
        const result = await $fetch<CreateResult>('/api/telemedicine/doctors', { method: 'POST', body: payload() })
        if (result.email_sent) {
            toast.add({
                title: 'Doctor created',
                description: `A 6-digit code to set their password was emailed to ${sentTo}. Send them the portal sign-in link to enter it.`,
                icon: 'i-ph-check-circle-duotone',
                color: 'green',
                timeout: 15000,
                actions: [portalLinkAction(sentTo)],
            })
        } else {
            toast.add({
                title: `Doctor created, but the access email failed: ${result.email_error}. Use “Resend access email” on the doctor page.`,
                icon: 'i-ph-warning-duotone',
                color: 'amber',
                timeout: 15000,
            })
        }
        dismiss(true)
        await navigateTo(`/telemedicine/doctors/${result.id}`)
    } catch (e: any) {
        const status = e?.statusCode
        const message = e?.data?.message
        if (status === 409) {
            form.value?.setErrors([{ path: 'email', message }])
        } else if (status === 422) {
            serverBlockers.value = e?.data?.data?.blockers ?? []
        } else {
            toast.add({ title: message || 'Could not create the doctor', icon: 'i-ph-warning-duotone', color: 'red' })
        }
    } finally {
        loading.value = false
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) emit('reload', true)
}
</script>
